import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  View,
  StatusBar,
  FlatList,
  Dimensions,
  ActivityIndicator,
  TouchableOpacity,
  Text,
  Button,
  Platform,
} from 'react-native';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { Video, ResizeMode, AVPlaybackStatus } from 'expo-av';
import QuizOverlay from '../QuizOverlay/QuizOverlay';
import { useAuth } from '../../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { faRepeat, faPlay } from '../../optimizations/OptimizedIcons';
import { ViewStyle } from 'react-native';
import styles from './style';
import { LinearGradient } from 'expo-linear-gradient';
import { Linking } from 'react-native';
import EndMessage from '../EndMessage/EndMessage';
import TriBoolean from '../../hooks/utilities/ThreeBoolean';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  QuizProps,
  VideoResponseData,
  VideoOrEndMessage,
  VideoData,
} from './QuizTypes';
import { Audio, InterruptionModeIOS, InterruptionModeAndroid } from 'expo-av';
import LoadingComponent from '../LoadingComponent/LoadingComponent';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import { useAuthenticatedApi } from '../../hooks/user/useAuthenticatedApi';
import { User } from '../../hooks/user/UserTypes';
import { useDebugLogger } from '../../hooks/utilities/useDebugLogger';

const useInitialURL = () => {
  const [url, setUrl] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const getUrlAsync = async () => {
      const initialUrl = await Linking.getInitialURL();
      setUrl(initialUrl);
      setProcessing(false);
    };

    getUrlAsync();
  }, []);

  return { url, processing };
};

// Create a single video component that handles its own lifecycle
const SingleVideoItem = React.memo<{
  item: VideoData,
  isVisible: boolean,
  onQuestionAnswered: () => void,
  index: number,
  totalCount: number,
  visibleIndexRef: React.RefObject<number>,
}>(({ item, isVisible, onQuestionAnswered, index, totalCount, visibleIndexRef }) => {
  const videoRef = useRef<Video>(null);
  const [showPlayButton, setShowPlayButton] = useState(false);
  const [showReplayButton, setShowReplayButton] = useState(false);
  const [commentsVisible, setCommentsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [questionAnswered, setQuestionAnswered] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const retryCount = useRef(0);
  const playAttempted = useRef(false);
  const { userProfile } = useAuth();
  const isFocused = useIsFocused();
  const insets = useSafeAreaInsets();
  const debug = useDebugLogger();
  const isChromeMobile = Platform.OS === 'web' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && /Chrome/i.test(navigator.userAgent);

  // Layout calculations
  const windowWidth = Dimensions.get('window').width;
  const windowHeight = Dimensions.get('window').height;
  const tabBarHeight = 55;
  const statusBarHeight = StatusBar.currentHeight || 0;
  const bottomInset = insets.bottom || 0;
  const availableHeight = windowHeight - statusBarHeight - tabBarHeight - bottomInset;

  // Calculate video style based on aspect ratio
  const getVideoStyle = useCallback((): ViewStyle => {
    const aspectRatioString = item.aspectRatio || '9:16';
    const [widthStr, heightStr] = aspectRatioString.split(':');
    const width = parseInt(widthStr, 10);
    const height = parseInt(heightStr, 10);
    const ratio = width / height;

    const isMobileFormat = height > width;
    if (!isMobileFormat) {
      return {
        width: '100%',
        height: '100%',
        alignSelf: 'center',
      };
    } else {
      const vidWidth = Math.min(windowWidth, windowHeight * ratio);
      const vidHeight = vidWidth / ratio;
      return {
        aspectRatio: ratio,
        width: vidWidth,
        height: vidHeight,
        alignSelf: 'center',
      };
    }
  }, [item.aspectRatio, windowWidth, windowHeight]);

  const videoStyle = getVideoStyle();

  // Check if user has blocked this video uploader
  const blockedList = userProfile?.blockedUserIds || [];
  const isBlocked = !!item.uploaderUsername && blockedList.includes(item.uploaderUsername);

  // Clear resources for video when no longer visible - helps with Chrome mobile
  useEffect(() => {
    if (!isVisible && videoRef.current && videoLoaded) {
      debug.log(`Video ${item.id} - no longer visible, pausing and potentially releasing resources`);
      videoRef.current.pauseAsync().catch(() => {});
      
      // For Chrome mobile, we're more aggressive about unloading to free resources
      if (isChromeMobile && index > 0 && visibleIndexRef.current !== undefined && 
          visibleIndexRef.current != null && Math.abs(index - visibleIndexRef.current) > 1) {
        debug.log(`Video ${item.id} - unloading as we're scrolled away on Chrome mobile`);
        videoRef.current.unloadAsync().catch(() => {});
        setVideoLoaded(false);
        setVideoReady(false);
      }
    }
  }, [isVisible, item.id, isChromeMobile, index, visibleIndexRef]);

  // Lifecycle management with improved memory handling
  useEffect(() => {
    // Set up audio when this video mounts
    Audio.setAudioModeAsync({
      allowsRecordingIOS: false,
      interruptionModeIOS: InterruptionModeIOS.DoNotMix,
      playsInSilentModeIOS: true,
      interruptionModeAndroid: InterruptionModeAndroid.DuckOthers,
      shouldDuckAndroid: true,
      staysActiveInBackground: false,
    }).catch(err => debug.error('Failed to set audio mode', err));

    // Start loading
    setIsLoading(true);
    retryCount.current = 0;
    playAttempted.current = false;

    // For Chrome mobile, we use a different strategy to conserve resources
    const shouldPreloadVideo = !isChromeMobile || index === 0 || index === 1;
    
    if (!shouldPreloadVideo) {
      debug.log(`Video ${item.id} - deferring load until visible on Chrome mobile`);
    }

    return () => {
      // Clean up video resources more aggressively
      if (videoRef.current) {
        try {
          videoRef.current.pauseAsync().catch(() => {});
          videoRef.current.unloadAsync().catch(() => {});
          setVideoLoaded(false);
          setVideoReady(false);
        } catch (err) {
          debug.error('Error cleaning up video:', err);
        }
      }
    };
  }, [item.id]);

  // Handle visibility changes with improved error handling and retries
  useEffect(() => {
    if (!videoRef.current) return;

    const handleVideoVisibilityChange = async () => {
      try {
        if (isVisible && isFocused) {
          // Video is now visible and tab is focused
          if (!videoLoaded) {
            debug.log(`Video ${item.id} - is visible but not loaded, loading now`);
            setIsLoading(true);
            // We'll rely on the VideoPlayer to load the video
            setVideoLoaded(true);
          } else if (!showReplayButton) {
            debug.log(`Video ${item.id} - visible and loaded, playing now`);
            playAttempted.current = true;
            await videoRef.current?.playAsync();
          }
        } else if (videoLoaded) {
          // Video is not visible or tab is not focused, pause it
          debug.log(`Video ${item.id} - not visible or focused, pausing`);
          await videoRef.current?.pauseAsync();
        }
      } catch (err) {
        debug.error(`Video ${item.id} - Error handling visibility change:`, err);
        
        // Retry logic for failed play attempts
        if (isVisible && isFocused && retryCount.current < 3) {
          retryCount.current++;
          debug.log(`Video ${item.id} - Retry ${retryCount.current}/3`);
          
          // Wait a moment and try again
          setTimeout(() => {
            if (videoRef.current && isVisible && isFocused) {
              videoRef.current.playAsync().catch(() => {});
            }
          }, 500);
        }
      }
    };

    // Execute visibility change logic
    handleVideoVisibilityChange();
  }, [isVisible, isFocused, showReplayButton, videoLoaded, item.id]);

  // Video playback status handler with better error recovery
  const handlePlaybackStatusUpdate = (status: AVPlaybackStatus) => {
    if (!status.isLoaded) {
      if (status.error) {
        debug.error(`Video ${item.id} - Playback error:`, status.error);
        
        // If we have an error and haven't exceeded retry count, attempt reload
        if (isVisible && retryCount.current < 3) {
          retryCount.current++;
          debug.log(`Video ${item.id} - Error, retrying load (${retryCount.current}/3)`);
          
          // Wait a moment and try to reload
          setTimeout(() => {
            if (videoRef.current) {
              videoRef.current.loadAsync({ uri: item.source }, {}, false)
                .catch(err => debug.error(`Video ${item.id} - Retry load failed:`, err));
            }
          }, 1000);
        } else {
          setShowPlayButton(true);
          setIsLoading(false);
        }
      } else {
        setShowPlayButton(true);
        setIsLoading(false);
      }
      return;
    }

    // Video is loaded successfully
    setIsLoading(false);
    setVideoReady(true);

    if (status.isPlaying) {
      setShowPlayButton(false);

      // Check if we need to pause at question point
      const pausePoint = item.pausePoint;
      if (pausePoint && status.positionMillis / 1000 >= pausePoint && !questionAnswered) {
        if (videoRef.current) {
          videoRef.current.pauseAsync().catch(() => {});
          setShowReplayButton(true);
        }
      }
    }
  };

  // Action handlers
  const handleReplay = () => {
    if (!videoRef.current) return;

    videoRef.current.setPositionAsync(0)
      .then(() => videoRef.current?.playAsync())
      .catch(err => debug.error('Failed to replay video', err));

    setShowReplayButton(false);
  };

  const handlePlay = () => {
    if (!videoRef.current) return;

    videoRef.current.playAsync()
      .then(() => {
        setShowPlayButton(false);
        playAttempted.current = true;
      })
      .catch(err => {
        debug.error('Failed to play video', err);
        
        // Retry logic
        if (retryCount.current < 3) {
          retryCount.current++;
          setTimeout(handlePlay, 500);
        }
      });
  };

  const handleAnswerSelect = () => {
    setQuestionAnswered(true);
    setShowReplayButton(false);
    onQuestionAnswered();

    if (videoRef.current && isFocused) {
      videoRef.current.playAsync().catch(() => {});
    }
  };

  const handleToggleComments = (isVisible: boolean) => {
    setCommentsVisible(isVisible);
  };

  // Specific handler for video loading completion
  const handleVideoLoad = () => {
    setIsLoading(false);
    setVideoLoaded(true);
    
    // For Chrome mobile, automatically try to play when loaded if this is visible
    if (isChromeMobile && isVisible && isFocused && videoRef.current && !playAttempted.current) {
      debug.log(`Video ${item.id} - loaded on Chrome mobile, auto-playing`);
      playAttempted.current = true;
      videoRef.current.playAsync().catch(err => {
        debug.error(`Video ${item.id} - Auto-play failed:`, err);
      });
    }
  };

  return (
    <View 
      style={{
        width: windowWidth,
        height: availableHeight,
        justifyContent: 'center',
        alignItems: 'center',
      }}
      accessible={true}
      accessibilityLabel={`Video: ${item.question || 'Quiz video'}`}
    >
      {isLoading && (
        <View 
          style={styles.loadingContainer}
          accessible={true}
          accessibilityRole="progressbar"
          accessibilityLabel="Loading video"
        >
          <ActivityIndicator size="large" color="#00ff00" />
        </View>
      )}

      <VideoPlayer
        source={{ uri: item.source }}
        style={videoStyle}
        videoStyle={videoStyle}
        videoId={item.id}
        isLooping={true}
        shouldPlay={isVisible && isFocused && !showReplayButton}
        resizeMode={ResizeMode.COVER}
        onLoad={handleVideoLoad}
        onPlaybackStatusUpdate={handlePlaybackStatusUpdate}
        ref={videoRef}
        commentsVisible={commentsVisible}
      />

      {showReplayButton && isVisible && (
        <TouchableOpacity 
          style={styles.replayButtonContainer} 
          onPress={handleReplay}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="Replay video"
          accessibilityHint="Restarts the current video from the beginning"
        >
          <FontAwesomeIcon icon={faRepeat} size={50} color="#FFF" />
        </TouchableOpacity>
      )}

      {showPlayButton && isVisible && (
        <TouchableOpacity 
          style={styles.playButtonContainer} 
          onPress={handlePlay}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="Play video"
          accessibilityHint="Starts playing the current video"
        >
          <FontAwesomeIcon icon={faPlay} size={50} color="#FFF" />
        </TouchableOpacity>
      )}

      <QuizOverlay
        mongoId={item._id}
        question={item.question as string}
        GCSId={item.id}
        answers={item.answers || []}
        isBlocked={isBlocked}
        onAnswerSelect={() => handleAnswerSelect()}
        correctAnswer={item.correctAnswer}
        uploaderUsername={item.uploaderUsername}
        isFromProfile={false}
        answersModalVisible={false}
        setAnswersModalVisible={() => {}}
        onVideoInteraction={() => {}}
        onCommentsToggle={handleToggleComments}
        onAnswersModalStateChange={() => {}}
        commentsVisible={commentsVisible}
      />
    </View>
  );
});

// Main Quiz component
const Quiz: React.FC<QuizProps> = () => {
  // State
  const [videoData, setVideoData] = useState<VideoOrEndMessage[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [fromDeepLink, setFromDeepLink] = useState(false);
  const [currentVisibleIndex, setCurrentVisibleIndex] = useState(0);
  
  // Refs for tracking component state and preventing race conditions
  const isMounted = useRef(true);
  const flatListRef = useRef<FlatList<VideoOrEndMessage>>(null);
  const loadedPages = useRef<Set<number>>(new Set());
  const isCurrentlyFetching = useRef(false);
  const authStatusChecked = useRef(false);
  const quizComponentId = useRef(`quiz-${Date.now()}`);
  const userProfileRef = useRef<User | null>(null);
  const visibleIndexRef = useRef<number>(0);
  
  // Added to track if we're on Chrome mobile
  const isChromeMobile = Platform.OS === 'web' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && /Chrome/i.test(navigator.userAgent);

  const debug = useDebugLogger();
  
  // Hooks and contextual data
  const { 
    userProfile, 
    isAuthenticated, 
    authInitialized,
    isAuthenticatedStable,
    authInitializedStable,
    getUserProfileStable
  } = useAuth();
  
  const { authenticatedApiCall, unauthenticatedApiCall } = useAuthenticatedApi();
  const [debugMessage, setDebugMessage] = useState<string>(""); 
  const insets = useSafeAreaInsets();
  const isFocused = useIsFocused();
  
  // Layout calculations
  const windowHeight = Dimensions.get('window').height;
  const windowWidth = Dimensions.get('window').width;
  const statusBarHeight = StatusBar.currentHeight || 0;
  const tabBarHeight = 55;
  const bottomInset = insets.bottom || 0;
  const availableHeight = windowHeight - statusBarHeight - tabBarHeight - bottomInset;

  // Track Quiz component lifecycle
  useEffect(() => {
    debug.log(`[Quiz ${quizComponentId.current}] Component mounted${isChromeMobile ? ' on Chrome mobile' : ''}`);
    isMounted.current = true;
    loadedPages.current.clear();
    
    return () => {
      debug.log(`[Quiz ${quizComponentId.current}] Component unmounting`);
      isMounted.current = false;
    };
  }, []);
  
  // Sync userProfileRef whenever userProfile changes
  useEffect(() => {
    if (userProfile) {
      debug.log(`[Quiz ${quizComponentId.current}] Syncing userProfileRef with userProfile: ${userProfile.username}`);
      userProfileRef.current = userProfile;
    } else {
      // Try to get from the stable accessor if state is null
      const stableProfile = getUserProfileStable();
      if (stableProfile) {
        debug.log(`[Quiz ${quizComponentId.current}] userProfile state is null, using stable profile: ${stableProfile.username}`);
        userProfileRef.current = stableProfile;
      }
    }
  }, [userProfile, getUserProfileStable]);
  
  // Track auth state changes for debugging
  useEffect(() => {
    debug.log(`[Quiz ${quizComponentId.current}] Auth state changed - initialized: ${authInitialized}, auth: ${isAuthenticated}`);
  }, [authInitialized, isAuthenticated]);
  
  // Watch for auth initialization and load videos once auth is ready
  useEffect(() => {
    // Use both state and stable ref to determine if auth is ready
    const isAuthReady = authInitialized || authInitializedStable();
    
    if (isAuthReady && !authStatusChecked.current) {
      authStatusChecked.current = true;
      debug.log(`[Quiz ${quizComponentId.current}] Auth initialized (${isAuthenticatedStable()}), loading videos...`);
      setDebugMessage(`Auth ready: ${isAuthenticatedStable()}`);
      checkForDeepLinkOrLoadVideos();
    } else if (!isAuthReady) {
      debug.log(`[Quiz ${quizComponentId.current}] Waiting for auth to initialize...`);
      setDebugMessage('Waiting for auth...');
    }
  }, [authInitialized]);
  
  // Check for deep link or load initial videos
  const checkForDeepLinkOrLoadVideos = async () => {
    try {
      const initialUrl = await Linking.getInitialURL();
      
      if (initialUrl) {
        // Try to parse as deep link
        try {
          const url = new URL(initialUrl);
          if (url.searchParams.has('videoId')) {
            const videoId = url.searchParams.get('videoId');
            if (videoId) {
              debug.log(`[Quiz ${quizComponentId.current}] Loading single video from deep link:`, videoId);
              await loadSingleVideo(videoId);
              return;
            }
          }
        } catch (e) {
          debug.error(`[Quiz ${quizComponentId.current}] Failed to parse deep link:`, e);
        }
      }
      
      // If we get here, load the normal feed
      fetchVideos(1);
      
    } catch (error) {
      debug.error(`[Quiz ${quizComponentId.current}] Error during initial navigation:`, error);
      fetchVideos(1);
    }
  };
  
  // Load a single video from a deep link
  const loadSingleVideo = async (videoId: string) => {
    setIsInitialLoading(true);
    setFromDeepLink(true);
    
    try {
      const response = await unauthenticatedApiCall(`videos/${videoId}`, 'GET');
      
      if (isMounted.current) {
        if (response) {
          setVideoData([response]);
          setCurrentVisibleIndex(0);
          visibleIndexRef.current = 0;
          setCurrentPage(1);
          loadedPages.current.add(1);
        } else {
          debug.error(`[Quiz ${quizComponentId.current}] Failed to load deep linked video`);
          // Fall back to regular feed
          await fetchVideos(1);
        }
      }
    } catch (error) {
      debug.error(`[Quiz ${quizComponentId.current}] Error loading deep linked video:`, error);
      // Fall back to regular feed
      await fetchVideos(1);
    } finally {
      if (isMounted.current) {
        setIsInitialLoading(false);
      }
    }
  };
  
  // Helper function to safely compare TriBoolean values
  const compareTrinaryValues = (a: TriBoolean, b: TriBoolean): boolean => {
    // Force numeric comparison to avoid any type issues
    return Number(a) === Number(b);
  };

  // Track current page for pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch videos with pagination - improved for better error handling and Chrome mobile
  const fetchVideos = async (pageNum: number) => {
    // Use both state and stable ref value to determine if auth is ready
    const isAuthReady = authInitialized || authInitializedStable();
    
    // If auth status hasn't been checked yet and this isn't the first page, wait
    if (!isAuthReady && !authStatusChecked.current && pageNum !== 1) {
      debug.log(`[Quiz ${quizComponentId.current}] Skipping fetch for page ${pageNum} - auth not initialized yet`);
      setDebugMessage(`Can't load page ${pageNum} - auth not ready`);
      return;
    }
    
    // For first page, we might want to wait for auth to be properly initialized
    if (pageNum === 1 && !isAuthReady) {
      debug.log(`[Quiz ${quizComponentId.current}] Waiting for auth before fetching first page`);
      // We'll let the auth initialization effect trigger the fetch
      return;
    }

    // Simplified auth check to be more reliable
    const isActuallyAuthenticated = 
      (authInitialized || authInitializedStable()) && 
      userProfile && 
      userProfile.username && 
      isAuthenticatedStable() !== TriBoolean.FALSE;
    
    debug.log(`[Quiz ${quizComponentId.current}] isActuallyAuthenticated for page ${pageNum}: ${isActuallyAuthenticated}`);
    debug.log(`[Quiz ${quizComponentId.current}] userProfile exists: ${!!userProfile}, username: ${userProfile?.username || 'none'}`);
    
    // Skip if already loaded or no more content
    if (loadedPages.current.has(pageNum)) {
      debug.log(`[Quiz ${quizComponentId.current}] Skipping fetch for page ${pageNum} - already loaded`);
      return;
    }
    
    if (hasReachedEnd) {
      debug.log(`[Quiz ${quizComponentId.current}] Skipping fetch for page ${pageNum} - reached end of content`);
      return;
    }
    
    if (isCurrentlyFetching.current) {
      debug.log(`[Quiz ${quizComponentId.current}] Skipping fetch for page ${pageNum} - already fetching another page`);
      return;
    }
    
    // Set loading state
    isCurrentlyFetching.current = true;
    if (pageNum === 1) {
      setIsInitialLoading(true);
    } else {
      debug.log(`[Quiz ${quizComponentId.current}] Setting pagination loading to TRUE for page ${pageNum}`);
      setIsPaginationLoading(true);
      setDebugMessage(`Loading page ${pageNum}...`);
    }
    
    debug.log(`=============================================`);
    debug.log(`[Quiz ${quizComponentId.current}] FETCHING VIDEOS FOR PAGE ${pageNum} (Auth: ${isAuthenticatedStable()})`);
    debug.log(`=============================================`);
    
    try {
      // Get the most reliable user profile information
      let stableUserProfile = userProfileRef.current || getUserProfileStable();
      
      if (!stableUserProfile?.username) {
        // Last resort - try to get directly from AsyncStorage
        try {
          const storedProfile = await AsyncStorage.getItem('userProfile');
          if (storedProfile) {
            debug.log(`[Quiz ${quizComponentId.current}] Restored profile from AsyncStorage`);
            stableUserProfile = JSON.parse(storedProfile);
            // Update our ref so we don't need to do this again
            userProfileRef.current = stableUserProfile;
          }
        } catch (error) {
          debug.error(`[Quiz ${quizComponentId.current}] Error reading from AsyncStorage:`, error);
        }
      }
      
      // Debug userProfile status
      debug.log(`[Quiz ${quizComponentId.current}] Fetching page ${pageNum} with profile:`, 
        stableUserProfile ? `Username: ${stableUserProfile.username}` : 'null');
      
      // Make appropriate API call based on auth status
      let response: VideoResponseData[] = [];
      
      if (stableUserProfile?.username) {
        debug.log(`[Quiz ${quizComponentId.current}] Making authenticated API call for page ${pageNum} with username ${stableUserProfile.username}`);
        
        // Prepare parameters from the stable profile
        const followingIds = stableUserProfile.followingUserIds || [];
        const watchedIds = Array.isArray(stableUserProfile.watchedVideos) 
          ? stableUserProfile.watchedVideos.map(item => item.videoId)
          : [];
        const blockedIds = stableUserProfile.blockedUserIds || [];
        
        const apiUrl = `videos?` +
          `followingUserIds=${JSON.stringify(followingIds)}` +
          `&watchedVideos=${JSON.stringify(watchedIds)}` +
          `&blockedUserIds=${JSON.stringify(blockedIds)}` +
          `&pageInput=${pageNum}`;
        
        debug.log(`[Quiz ${quizComponentId.current}] API URL: ${apiUrl}`);
        
        // Use authenticated API call with retries
        try {
          response = await authenticatedApiCall(
            apiUrl,
            'GET',
            {},
            false
          );
        } catch (error) {
          debug.error(`[Quiz ${quizComponentId.current}] Error with auth API call, retrying:`, error);
          
          // Retry once with error handling
          try {
            response = await authenticatedApiCall(
              apiUrl,
              'GET',
              {},
              false
            );
          } catch (retryError) {
            debug.error(`[Quiz ${quizComponentId.current}] Retry also failed:`, retryError);
            // Fall back to unauthenticated API call as last resort
            response = await unauthenticatedApiCall(
              `videos/most-popular`,
              'GET',
              null,
              { params: { pageInput: pageNum } }
            );
          }
        }
        
        debug.log(`[Quiz ${quizComponentId.current}] Authenticated API response for page ${pageNum}:`, 
          response ? `Received ${Array.isArray(response) ? response.length : 0} videos` : 'No response');
      } else if (isAuthenticatedStable() === TriBoolean.FALSE) {
        debug.log(`[Quiz ${quizComponentId.current}] Making unauthenticated API call for page ${pageNum}`);
        
        // For GET requests with Axios, we need to use the params option
        response = await unauthenticatedApiCall(
          `videos/most-popular`,
          'GET',
          null,
          { params: { pageInput: pageNum } }
        );
        
        debug.log(`[Quiz ${quizComponentId.current}] Unauthenticated API response for page ${pageNum}:`, 
          response ? `Received ${Array.isArray(response) ? response.length : 0} videos` : 'No response');
      } else {
        debug.log(`[Quiz ${quizComponentId.current}] Cannot determine auth state or profile for page ${pageNum}`);
        setDebugMessage(`Auth unclear - retrying with unauthenticated call`);
        
        // As a fallback, try unauthenticated call
        response = await unauthenticatedApiCall(
          `videos/most-popular`,
          'GET',
          null,
          { params: { pageInput: pageNum } }
        );
      }
      
      // Process response
      if (isMounted.current) {
        // Debug response
        debug.log(`[Quiz ${quizComponentId.current}] Processing response for page ${pageNum}`);
        debug.log(`Response type: ${typeof response}`);
        debug.log(`Is array: ${Array.isArray(response)}`);
        debug.log(`Length: ${Array.isArray(response) ? response.length : 'N/A'}`);
        
        // Check for end of content
        if (!response) {
          debug.log(`[Quiz ${quizComponentId.current}] No response for page ${pageNum}`);
          loadedPages.current.add(pageNum); // Still mark it as loaded to prevent repeatedly fetching
          setHasReachedEnd(true);
          isCurrentlyFetching.current = false;
          setIsPaginationLoading(false);
          setDebugMessage(`No videos for page ${pageNum} - end reached`);
          return;
        }
        
        if (!Array.isArray(response)) {
          debug.log(`[Quiz ${quizComponentId.current}] API returned non-array response for page ${pageNum}:`, response);
          
          // If we got a true/false/empty, treat it as end of content
          if (typeof response === 'boolean' || response === '') {
            debug.log(`[Quiz ${quizComponentId.current}] Boolean/empty response for page ${pageNum} - treating as end of content`);
            loadedPages.current.add(pageNum); // Still mark it as loaded
            setHasReachedEnd(true);
            isCurrentlyFetching.current = false;
            setIsPaginationLoading(false);
            setDebugMessage(`End of content reached at page ${pageNum}`);
            return;
          }
          
          // For other non-array responses, log error but don't block further attempts
          debug.error(`[Quiz ${quizComponentId.current}] Invalid response format for page ${pageNum}:`, response);
          isCurrentlyFetching.current = false;
          setIsPaginationLoading(false);
          setDebugMessage(`Error: Invalid response for page ${pageNum}`);
          return;
        }
        
        if (response.length === 0) {
          debug.log(`[Quiz ${quizComponentId.current}] End of content reached at page ${pageNum} - no videos returned`);
          setHasReachedEnd(true);
          isCurrentlyFetching.current = false;
          setIsPaginationLoading(false);
          setDebugMessage(`Reached end at page ${pageNum}`);
          return;
        }

        // Check if we have less than the expected items per page (5)
        if (response.length < 5) {
          debug.log(`[Quiz ${quizComponentId.current}] Partial page received (${response.length} items) for page ${pageNum}`);
        }
        
        // Mark page as loaded
        loadedPages.current.add(pageNum);
        debug.log(`[Quiz ${quizComponentId.current}] Marked page ${pageNum} as loaded. Loaded pages: ${[...loadedPages.current].join(', ')}`);
        
        // Update page state
        const newCurrentPage = Math.max(currentPage, pageNum);
        if (newCurrentPage !== currentPage) {
          debug.log(`[Quiz ${quizComponentId.current}] Updating current page from ${currentPage} to ${newCurrentPage}`);
          setCurrentPage(newCurrentPage);
        }
        
        // Check response data for first few videos
        if (response.length > 0) {
          debug.log(`[Quiz ${quizComponentId.current}] First video in response:`, {
            id: response[0].id,
            source: response[0].source ? response[0].source.substring(0, 30) + '...' : 'No source'
          });
        }
        
        // Update video data
        setVideoData(prevData => {
          debug.log(`[Quiz ${quizComponentId.current}] Updating video data. Previous count: ${prevData.length}`);
          
          // If first page, replace data
          if (pageNum === 1) {
            debug.log(`[Quiz ${quizComponentId.current}] First page - replacing all data with ${response.length} videos`);
            return [...response];
          }
          
          // For subsequent pages, ensure we're not adding duplicates
          const existingIds = new Set(prevData.map(item => 'id' in item ? item.id : null));
          debug.log(`[Quiz ${quizComponentId.current}] Found ${existingIds.size} existing video IDs`);
          
          const newVideos = response.filter(video => !existingIds.has(video.id));
          debug.log(`[Quiz ${quizComponentId.current}] After filtering duplicates, adding ${newVideos.length} new videos`);
          
          // Append new videos
          const updatedData = [...prevData, ...newVideos];
          debug.log(`[Quiz ${quizComponentId.current}] New total video count: ${updatedData.length}`);
          setDebugMessage(`Loaded ${newVideos.length} videos (total: ${updatedData.length})`);
          return updatedData;
        });
        
        debug.log(`[Quiz ${quizComponentId.current}] Added ${response.length} videos from page ${pageNum}`);
      } else {
        debug.log(`[Quiz ${quizComponentId.current}] Component unmounted before processing response for page ${pageNum}`);
      }
    } catch (error) {
      debug.error(`[Quiz ${quizComponentId.current}] Error fetching videos for page ${pageNum}:`, error);
      setDebugMessage(
        `Error loading page ${pageNum}: ${
          typeof error === 'object' && error !== null && 'message' in error
            ? (error as { message: string }).message
            : String(error)
        }`
      );
    } finally {
      // Reset loading states
      if (isMounted.current) {
        setIsInitialLoading(false);
        debug.log(`[Quiz ${quizComponentId.current}] Setting pagination loading to FALSE for page ${pageNum}`);
        setIsPaginationLoading(false);
        isCurrentlyFetching.current = false;
      } else {
        debug.log(`[Quiz ${quizComponentId.current}] Component unmounted while fetching page ${pageNum}`);
      }
      debug.log(`=============================================`);
      debug.log(`[Quiz ${quizComponentId.current}] FINISHED PROCESSING PAGE ${pageNum}`);
      debug.log(`=============================================`);
    }
  };
  
  // Handle the user scrolling to the end
  const handleEndReached = useCallback(() => {
    if (!fromDeepLink && !isCurrentlyFetching.current && !hasReachedEnd) {
      debug.log(`[Quiz ${quizComponentId.current}] End reached, loading page ${currentPage + 1}`);
      setDebugMessage(`End reached -> loading page ${currentPage + 1}`);
      fetchVideos(currentPage + 1);
    } else {
      debug.log(`[Quiz ${quizComponentId.current}] End reached but not loading: fromDeepLink=${fromDeepLink}, fetching=${isCurrentlyFetching.current}, end=${hasReachedEnd}`);
      setDebugMessage(`EndReach: No load (DL:${fromDeepLink?'Y':'N'}, F:${isCurrentlyFetching.current?'Y':'N'}, E:${hasReachedEnd?'Y':'N'})`);
    }
  }, [currentPage, fromDeepLink, hasReachedEnd]);
  
  // Improved viewability handler with better tracking for Chrome mobile
  const handleViewableItemsChanged = useCallback(({ 
    viewableItems 
  }: { 
    viewableItems: Array<{
      item: VideoOrEndMessage;
      key: string;
      index: number | null;
      isViewable: boolean;
      section?: any;
    }>;
    changed: Array<any>;
  }) => {
    if (viewableItems && viewableItems.length > 0) {
      const firstVisible = viewableItems[0];
      if (firstVisible.index !== null && firstVisible.index !== undefined) {
        // Update both the state and the ref for better synchronization
        setCurrentVisibleIndex(firstVisible.index);
        visibleIndexRef.current = firstVisible.index;
        
        debug.log(`[Quiz ${quizComponentId.current}] Visible item changed to index ${firstVisible.index}`);
        
        // For Chrome mobile, force a small timeout before initializing video play
        // This helps with the race condition between visibility and video resource loading
        if (isChromeMobile) {
          setTimeout(() => {
            if (visibleIndexRef.current === firstVisible.index) {
              debug.log(`[Quiz ${quizComponentId.current}] Chrome mobile: reaffirming visible index ${firstVisible.index}`);
            }
          }, 50);
        }
        
        // Preload next page when getting close to the end
        if (!hasReachedEnd && !isCurrentlyFetching.current && !fromDeepLink) {
          const totalItems = videoData.length;
          const remainingItems = totalItems - firstVisible.index;
          
          if (remainingItems <= 2) {
            const nextPage = currentPage + 1;
            if (!loadedPages.current.has(nextPage)) {
              debug.log(`[Quiz ${quizComponentId.current}] Preloading next page ${nextPage} as we're near the end`);
              fetchVideos(nextPage);
            }
          }
        }
      }
    }
  }, [currentPage, videoData.length, hasReachedEnd, fromDeepLink, isChromeMobile]);
  
  // Improved viewability config for better detection on Chrome mobile
  const viewabilityConfig = {
    itemVisiblePercentThreshold: isChromeMobile ? 50 : 80, // More sensitive threshold for Chrome mobile
    minimumViewTime: isChromeMobile ? 100 : 200, // Faster recognition on Chrome mobile
  };
  
  const viewabilityConfigCallbackPairs = useRef([
    { viewabilityConfig, onViewableItemsChanged: handleViewableItemsChanged }
  ]);
  
  // Update callback reference
  useEffect(() => {
    if (viewabilityConfigCallbackPairs.current[0]) {
      viewabilityConfigCallbackPairs.current[0].onViewableItemsChanged = handleViewableItemsChanged;
    }
  }, [handleViewableItemsChanged]);
  
  // Helper functions for FlatList with optimizations for Chrome mobile
  const getItemLayout = useCallback((data: ArrayLike<VideoOrEndMessage> | null | undefined, index: number) => ({
    length: availableHeight,
    offset: availableHeight * index,
    index,
  }), [availableHeight]);
  
  const keyExtractor = useCallback((item: VideoOrEndMessage, index: number) => 
    typeof item.id === 'string' ? `${item.id}-${index}` : `end-${index}`, []);
  
  const handleQuestionAnswered = useCallback(() => {
    debug.log(`[Quiz ${quizComponentId.current}] Question answered`);
  }, []);
  
  // Render list items with better Chrome mobile support
  const renderItem = useCallback(({ 
    item, 
    index 
  }: { 
    item: VideoOrEndMessage; 
    index: number 
  }) => {
    // For debugging - less verbose to avoid log spam
    if (index === 0 || index % 5 === 0) {
      debug.log(`[Quiz ${quizComponentId.current}] Rendering item at index ${index}`);
    }
    
    // Handle end message
    if ('isEndMessage' in item) {
      return (
        <LinearGradient
          colors={['#141E30', '#243B55']}
          style={{ height: availableHeight, justifyContent: 'center', alignItems: 'center' }}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          accessible={true}
          accessibilityRole="text"
          accessibilityLabel="End of content message"
        >
          <Text 
            style={styles.endMessageText}
            accessible={true}
            accessibilityRole="text"
          >
            That's it! Invite your friends for more content!
          </Text>
        </LinearGradient>
      );
    }
    
    // Handle video with explicit width to fix Chrome mobile rendering
    const isVisible = index === currentVisibleIndex;
    
    return (
      <SingleVideoItem
        item={item}
        isVisible={isVisible}
        onQuestionAnswered={handleQuestionAnswered}
        index={index}
        totalCount={videoData.length}
        visibleIndexRef={visibleIndexRef}
      />
    );
  }, [currentVisibleIndex, availableHeight, handleQuestionAnswered, videoData.length]);
  
  // Render footer component
  const renderFooter = useCallback(() => {
    if (isPaginationLoading) {
      return (
        <View 
          style={{ height: availableHeight, justifyContent: 'center', alignItems: 'center' }}
          accessible={true}
          accessibilityRole="progressbar"
          accessibilityLabel="Loading more videos"
        >
          <ActivityIndicator size="large" color="#00ff00" />
          <Text 
            style={{ color: 'white', marginTop: 10 }}
            accessible={true}
            accessibilityRole="text"
          >
            Loading more videos...
          </Text>
        </View>
      );
    }
    
    if (hasReachedEnd) {
      return (
        <View 
          style={{ height: availableHeight, justifyContent: 'center', alignItems: 'center' }}
          accessible={true}
          accessibilityRole="text"
          accessibilityLabel="End of content"
        >
          <EndMessage />
        </View>
      );
    }
    
    // Empty spacer to maintain consistent height
    return <View style={{ height: availableHeight }} />;
  }, [isPaginationLoading, hasReachedEnd, availableHeight]);
  
  // Handle loading state
  if (!isChromeMobile && isInitialLoading && videoData.length === 0) {
    return <LoadingComponent />;
  }
  
  // Debug information for development only
  const debugInfo = {
    currentPage,
    totalVideos: videoData.length,
    loadedPages: Array.from(loadedPages.current).join(', '),
    currentIndex: currentVisibleIndex,
    hasReachedEnd,
    isPaginationLoading,
    authInitialized: authInitializedStable(),
    isAuthenticated: isAuthenticatedStable(),
    isChromeMobile
  };
  
  return (
    <View 
      style={styles.quizContainer}
      accessible={true}
      accessibilityLabel="Quiz videos feed"
    >
      {/* Debug overlay (development only) */}
      {__DEV__ && (
        <View 
          style={{ 
            position: 'absolute', 
            top: 40, 
            right: 10, 
            zIndex: 999, 
            backgroundColor: 'rgba(0,0,0,0.7)', 
            padding: 5,
            borderRadius: 5
          }}
          accessible={true}
          accessibilityRole="text"
          accessibilityLabel="Debug information"
        >
          <Text style={{ color: 'white', fontSize: 10 }}>
            Page: {debugInfo.currentPage}, Videos: {debugInfo.totalVideos}{'\n'}
            Index: {debugInfo.currentIndex}, Loaded: {debugInfo.loadedPages}{'\n'}
            End: {debugInfo.hasReachedEnd ? 'Yes' : 'No'}, Loading: {debugInfo.isPaginationLoading ? 'Yes' : 'No'}{'\n'}
            Auth Ready: {debugInfo.authInitialized ? 'Yes' : 'No'}, Auth: {debugInfo.isAuthenticated}{'\n'}
            Chrome Mobile: {debugInfo.isChromeMobile ? 'Yes' : 'No'}{'\n'}
            {debugMessage}
          </Text>
          
          <TouchableOpacity 
            style={{ 
              marginTop: 5, 
              backgroundColor: '#444', 
              padding: 3, 
              borderRadius: 3 
            }}
            onPress={() => {
              const nextPage = currentPage + 1;
              setDebugMessage(`Manual fetch page ${nextPage}`);
              fetchVideos(nextPage);
            }}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel="Force load next page"
          >
            <Text style={{ color: 'white', fontSize: 8, textAlign: 'center' }}>
              Force Load Next Page
            </Text>
          </TouchableOpacity>
        </View>
      )}
      
      {/* Video list */}
      <FlatList
        data={videoData}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        getItemLayout={getItemLayout}
        removeClippedSubviews={Platform.OS !== 'web'} // Disable on web for better Chrome compatibility
        pagingEnabled={true}
        windowSize={isChromeMobile ? 2 : 3} // Smaller window size for Chrome mobile to reduce memory usage
        initialNumToRender={2}
        maxToRenderPerBatch={isChromeMobile ? 1 : 3} // Render fewer items at once on Chrome mobile
        updateCellsBatchingPeriod={isChromeMobile ? 50 : 100} // Faster updates on Chrome mobile
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.5}
        viewabilityConfigCallbackPairs={viewabilityConfigCallbackPairs.current}
        showsVerticalScrollIndicator={false}
        ListFooterComponent={renderFooter}
        ref={flatListRef}
        accessible={true}
        accessibilityRole="list"
        accessibilityLabel="Video Feed"
        onScrollToIndexFailed={(info) => {
          debug.log(`[Quiz ${quizComponentId.current}] Failed to scroll to index ${info.index}`, info);
          setTimeout(() => {
            if (flatListRef.current && videoData.length > 0) {
              flatListRef.current.scrollToIndex({
                index: Math.min(info.index, videoData.length - 1),
                animated: false
              });
            }
          }, 100);
        }}
        // Additional props for Chrome mobile optimization
        onMomentumScrollEnd={() => {
          if (isChromeMobile) {
            debug.log(`[Quiz ${quizComponentId.current}] Momentum scroll ended at index ${currentVisibleIndex}`);
            // Force recheck of visible video after scroll momentum ends
            const visibleIndex = visibleIndexRef.current;
            // Small timeout to ensure rendering is complete
            setTimeout(() => {
              if (visibleIndex === visibleIndexRef.current) {
                debug.log(`[Quiz ${quizComponentId.current}] Chrome mobile: reaffirming visible index ${visibleIndex} after scroll`);
                // This helps ensure the video player reacts to the final scroll position
                setCurrentVisibleIndex(visibleIndex);
              }
            }, 50);
          }
        }}
      />
    </View>
  );
};

export default Quiz;